import * as flashActions from 'pkg/actions/flashes';

import * as endpoints from 'pkg/api/endpoints/auto';
import type { LibraryTag } from 'pkg/api/models/library_tag';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';

export interface LibraryTagPayload {
	name?: string;
	// Empty string clears the parent, making the tag top-level.
	parentPublicId?: string;
	// #rrggbb. Required by the API when the tag is (or becomes) top-level;
	// must be omitted for children, which inherit their parent's color.
	color?: string;
}

// conflict is set on 409 responses (name clash with a tag visible to the
// group). Callers in modal contexts render it inline instead of flashing.
export interface LibraryTagMutationResult {
	ok: boolean;
	conflict?: boolean;
	record?: LibraryTag;
}

function flashCrash(): void {
	const err = crash();

	flashActions.show({
		title: err.title,
		message: err.description,
	});
}

async function toMutationResult(
	response: Response
): Promise<LibraryTagMutationResult> {
	if (!response.ok) {
		if (response.status === 409) {
			return { ok: false, conflict: true };
		}

		flashCrash();

		return { ok: false };
	}

	return { ok: true, record: await response.json() };
}

export async function create(
	organizationId: number,
	groupId: number,
	payload: LibraryTagPayload
): Promise<LibraryTagMutationResult> {
	const response = await sdk.post(
		endpoints.LibraryTags.Create(organizationId, groupId),
		{},
		payload
	);

	return toMutationResult(response);
}

export async function update(
	organizationId: number,
	libraryTagId: string,
	payload: LibraryTagPayload
): Promise<LibraryTagMutationResult> {
	const response = await sdk.patch(
		endpoints.LibraryTags.Update(organizationId, libraryTagId),
		{},
		payload
	);

	return toMutationResult(response);
}

export async function destroy(
	organizationId: number,
	libraryTagId: string
): Promise<boolean> {
	const response = await sdk.destroy(
		endpoints.LibraryTags.Delete(organizationId, libraryTagId),
		{}
	);

	if (!response.ok) {
		flashCrash();

		return false;
	}

	return true;
}

export interface LibraryTagMergePayload {
	// The tag that absorbs the others; it keeps its own name, color, parent
	// and sort order.
	survivorPublicId: string;
	absorbedPublicIds: string[];
}

// The response (the survivor with recomputed usage counts) is deliberately
// unused: the settings page refreshes the whole tree after every mutation,
// which also picks up the counts in the page's current scope.
// Failures are not flashed — the merge modal renders them inline — and
// network-level rejections (sdk.post throws on those) fold into the same
// false so the modal never hangs on a dead connection.
export async function merge(
	organizationId: number,
	payload: LibraryTagMergePayload
): Promise<boolean> {
	try {
		const response = await sdk.post(
			endpoints.LibraryTags.Merge(organizationId),
			{},
			payload
		);

		return response.ok;
	} catch {
		return false;
	}
}

export interface LegacyTagPreviewEntry {
	// The transformed library tag name (`counter_attack` → `Counter attack`).
	name: string;
	// The raw legacy names folding into this entry; more than one means the
	// case- and accent-insensitive fold collapsed them.
	legacyNames: string[];
	// Set when the name already exists in the organization's root library: the
	// migration reuses that tag wherever it lives rather than creating a
	// duplicate under the parent.
	mergesIntoPublicId?: string;
	exerciseCount: number;
	sessionCount: number;
	// Frozen per-block-item tag copies inside sessions — kept out of
	// exerciseCount because one exercise can appear in many sessions.
	sessionItemCount: number;
	videoClipCount: number;
}

export interface LegacyTagPreview {
	// Localized name of the parent the imported tags land under.
	parentName: string;
	tags: LegacyTagPreviewEntry[];

	// Distinct content items across all tags, so smaller than the summed
	// per-entry counts whenever content carries more than one tag.
	exerciseCount: number;
	sessionCount: number;
	sessionItemCount: number;
	videoClipCount: number;

	// True once the organization has migrated or dismissed the migration — the
	// entry points stay hidden then.
	migrated: boolean;
}

export interface LegacyTagMigrationResult {
	tagsCreated: number;
	tagsMerged: number;
	linksCreated: number;
	startedFresh: boolean;
}

export interface LegacyMigrationResponse {
	ok: boolean;
	result?: LegacyTagMigrationResult;
}

// Runs the legacy tag migration, or dismisses it when startFresh is set.
// Failures are not flashed — the migration modal renders them inline — and
// network-level rejections (sdk.post throws on those) fold into the same
// { ok: false } so the modal never hangs on a dead connection.
export async function migrateLegacy(
	organizationId: number,
	startFresh: boolean
): Promise<LegacyMigrationResponse> {
	try {
		const response = await sdk.post(
			endpoints.LibraryTags.LegacyMigrationExecute(organizationId),
			{},
			{ startFresh }
		);

		if (!response.ok) {
			return { ok: false };
		}

		return { ok: true, result: await response.json() };
	} catch {
		return { ok: false };
	}
}

export async function reorder(
	organizationId: number,
	groupId: number,
	publicIds: string[]
): Promise<boolean> {
	const response = await sdk.put(
		endpoints.LibraryTags.Reorder(organizationId, groupId),
		{},
		{ publicIds }
	);

	if (!response.ok) {
		flashCrash();

		return false;
	}

	return true;
}
